import moment from "moment";
import * as React from "react";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useNavigate, useParams } from "react-router";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import ToggleButton from "src/components/common/Buttons/ToggleButton";
import Tree, { ITree } from "src/components/common/Tree/Tree";
import { Link } from "src/components/routing";
import { GroupPersonnelsBreadcrumbs } from "src/helpers/breadcrumbHelper";
import * as GroupActions from "../../../actions/groups";
import * as NavigationActions from "../../../actions/navigation";
import * as TrialPersonnelActions from "../../../actions/trialPersonnel";
import { RestrictedLayout } from "../../../components/layouts/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { GroupSubMenu } from "../../../helpers/subMenuHelper";
import { ITmdState } from "../../../reducers/index";

import "./GroupPersonnelsPage.scss";

interface GroupPersonnelsPageProps {
    group: Dtos.Group;
    loadingGroup: boolean;
    loadGroupSuccess: boolean;
    loadGroupFailure: boolean;
    loadGroupErrorCode: string;
    trialPersonnel: Dtos.TrialPersonnel[];
    loadingTrialPersonnel: boolean;
    loadTrialPersonnelSuccess: boolean;
    loadTrialPersonnelFailure: boolean;
    loadTrialPersonnelErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearGroup: GroupActions.IGroupClearActionCreator;
    loadGroup: GroupActions.IGroupLoadByIdActionCreator;
    clearTrialPersonnels: TrialPersonnelActions.ITrialPersonnelClearActionCreator;
    loadTrialPersonnels: TrialPersonnelActions.ITrialPersonnelsLoadByGroupIdActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const GroupPersonnelsPage: FC<GroupPersonnelsPageProps> = ({
    group,
    loadingGroup,
    loadGroupSuccess,
    loadGroupFailure,
    loadGroupErrorCode,
    trialPersonnel,
    loadingTrialPersonnel,
    loadTrialPersonnelSuccess,
    loadTrialPersonnelFailure,
    loadTrialPersonnelErrorCode,
    permissions,
    clearGroup,
    loadGroup,
    clearTrialPersonnels,
    loadTrialPersonnels,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { groupId } = useParams<{ groupId: string }>();
    const groupIdNumber = parseInt(groupId, 10);

    //
    // -- States --
    const [showCeased, setShowCeased] = useState<boolean>(false);
    const [treeData, setTreeData] = useState<ITree | null>(null);
    const [initalizedTreeData, setInitalizedTreeData] = useState<boolean>(false);
    const [toggleState, setToggleState] = useState<string[]>([]);
    const [creatingTree, setCreatingTree] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- Variables --
    const showGroupPersonnels =
        !loadingGroup &&
        loadGroupSuccess &&
        !loadingTrialPersonnel &&
        loadTrialPersonnelSuccess &&
        !creatingTree;


    // Load Group & Personnel
    useEffect(() => {
        if (groupIdNumber && !group) {
            setBreadcrumbs(GroupPersonnelsBreadcrumbs(groupIdNumber));

            setTreeData(null);

            loadGroup(groupIdNumber);
            loadTrialPersonnels(groupIdNumber, undefined);
        }
    }, [
        groupIdNumber,
        group,
        setBreadcrumbs,
        GroupPersonnelsBreadcrumbs,
        setTreeData,
        loadGroup,
        loadTrialPersonnels
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadGroupFailure) {
            navigate(`/error/${loadGroupErrorCode}`);
        }
        if (loadTrialPersonnelFailure) {
            navigate(`/error/${loadTrialPersonnelErrorCode}`);
        }
    }, [
        loadGroupFailure, 
        loadGroupErrorCode, 
        loadTrialPersonnelFailure, 
        loadTrialPersonnelErrorCode, 
        navigate
    ]);

    // Update breadcrumbs & submenu
    useEffect(() => {
        if (groupIdNumber && group) {
            setDrawerItems(GroupSubMenu(groupIdNumber, permissions));
            setBreadcrumbs(GroupPersonnelsBreadcrumbs(groupIdNumber, group?.name));
        }
    }, [
        groupIdNumber,
        group,
        setDrawerItems,
        GroupSubMenu,
        permissions,
        setBreadcrumbs,
        GroupPersonnelsBreadcrumbs
    ]);

    // Cleanup effect to clear the group & personnel on component unmount
    useEffect(() => {
        return () => {
            setTreeData(null);
            clearGroup();
            clearTrialPersonnels();
        };
    }, [clearGroup, clearTrialPersonnels, setTreeData]);

    // Create tree
    useEffect(() => {
        if (trialPersonnel && !initalizedTreeData) {
            setCreatingTree(true);

            // If no group personnel return
            if (trialPersonnel?.length == 0) {
                setCreatingTree(false);
                return;
            }

            let uniqueRoles: Dtos.Role[] = Array.from(
                new Map(
                    trialPersonnel
                        ?.filter((x) => showCeased ? true : !x.dateCeased)
                        .map((x) => [x.role.id, x.role])
                ).values()
            );

            const createChildNodes = (roleId, showCeased) => {
                let children = trialPersonnel
                    ?.filter((y) => y.roleId == roleId && (showCeased ? true : !y.dateCeased))
                    ?.sort((a, b) => a?.id - b?.id)
                    ?.map((y) => ({
                        id: `personnel-${y?.id}`,
                        data: (
                            <div className={`form-row ${showCeased && y.dateCeased ? "ceased" : ""}`}>
                                <div className="col-1">
                                    <FontAwesomeIcon icon={FontAwesomeIcons.Duotone.USER} />
                                </div>
                                <div className="col">
                                    {
                                        y?.personnel?.displayWithSalutation
                                    }
                                </div>
                                <div className="col">
                                    {
                                        y?.dateStarted
                                            ? moment(y?.dateStarted, "YYYY-MM-DD HH:mm:ss.SSSSSSS Z").format("DD/MM/YYYY")
                                            : ""
                                    }
                                </div>
                                <div className="col">
                                    {
                                        y?.dateCeased
                                            ? moment(y?.dateCeased, "YYYY-MM-DD HH:mm:ss.SSSSSSS Z").format("DD/MM/YYYY")
                                            : ""
                                    }
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: false,
                    })) ?? [];

                // Add table header
                if (children?.length > 0) {
                    children.unshift({
                        id: `tableheader`,
                        data: (
                            <div className="form-row">
                                <div className="col-1"></div>
                                <div className="col">
                                    <strong>Name</strong>
                                </div>
                                <div className="col">
                                    <strong>Date Started</strong>
                                </div>
                                <div className="col">
                                    <strong>Date Ceased</strong>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: false,
                    });
                }

                return children;
            };

            let treeNodes =
                uniqueRoles
                    ?.sort((a, b) => a?.value?.localeCompare(b?.value))
                    ?.map((x) => ({
                        id: `role-${x.id}`,
                        data: (
                            <div className="d-flex align-items-center">
                                <FontAwesomeIcon icon={FontAwesomeIcons.Duotone.ROLE} />
                                <span
                                    className="pl-4 mb-0"
                                    style={{ width: "275px" }}
                                >
                                    {x?.value}
                                </span>
                                <div
                                    className="d-flex align-items-center justify-content-center"
                                    style={{ width: "50px" }}
                                >
                                    <span className="badge badge-pill badge-secondary ml-4 mb-0">
                                        {
                                            trialPersonnel?.filter(
                                                (y) =>
                                                    showCeased
                                                        ? y.roleId == x.id
                                                        : y.roleId == x.id &&
                                                            !y.dateCeased
                                            )?.length
                                        }
                                    </span>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: true,
                        subTree: {
                            children: [...createChildNodes(x.id, showCeased)],
                        },
                    })) ?? [];

            // Initialize tree toggle
            const expanded = treeNodes.map((z) => z.id);

            setToggleState(expanded);
            setTreeData({ children: treeNodes });
            setInitalizedTreeData(true);
            setCreatingTree(false);
        }
    }, [
        trialPersonnel,
        initalizedTreeData,
        treeData,
        setTreeData,
        setToggleState,
        setInitalizedTreeData,
        showCeased,
        setCreatingTree,
    ]);

    const handleToggle = useCallback(
        (id: string, navigateFlag?: boolean) => {
            if (navigateFlag) {
                if (
                    id.includes("personnel") &&
                    AuthenticationHelper.hasPermission(
                        permissions,
                        Dtos.Permission.TrialPersonnelRoleView
                    )
                ) {
                    navigate(`/groups/${groupIdNumber}/personnel/${id.replace("personnel-", "")}/details`);
                }
                if (id.includes("role")) {
                    setToggleState((prev) =>
                        prev.includes(id)
                            ? prev.filter((nodeId) => nodeId !== id)
                            : [...prev, id]
                    );
                }
            } else {
                setToggleState((prev) =>
                    prev.includes(id)
                        ? prev.filter((nodeId) => nodeId !== id)
                        : [...prev, id]
                );
            }
        },
        [
            setToggleState,
            navigate,
            groupIdNumber,
            permissions,
        ]
    );

    const onChange = (newValue) => {
        setInitalizedTreeData(false);
        setShowCeased(newValue);
    };

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className="col-md-12 col-lg-auto mb-2">
                        <h1>Group Personnel</h1>
                    </div>
                    <div
                        className="col-md-12 col-lg d-flex justify-content-md-start justify-content-lg-end align-items-stretch mb-2 mb-lg-0"
                        style={{ height: "38px" }}
                    >
                        { 
                            AuthenticationHelper.hasPermission(
                                permissions,
                                Dtos.Permission.TrialPersonnelRoleCreate,
                                null,
                                null
                            ) && (
                                <>
                                    <ToggleButton
                                        id="toggle-button-ceased"
                                        checked={showCeased}
                                        onChange={onChange}
                                        labelA="Hide ceased personnel"
                                        labelB="Show ceased personnel"
                                        style={{ minWidth: "200px" }}
                                    />
                                    <a
                                        className="btn btn-info mr-2"
                                        target="_blank"
                                        href={encodeURI(
                                            `/api/personnelrole/export/0/0/${groupIdNumber}/0`
                                        )}
                                    >
                                        <FontAwesomeIcon icon={FontAwesomeIcons.Solid.CLOUD_DOWNLOAD} fixedWidth />
                                    </a>
                                    <Link
                                        className={"btn btn-primary"}
                                        url={`/groups/${groupIdNumber}/personnel/create`}
                                    >
                                        Create
                                    </Link>
                                </>
                            )
                        }
                    </div>
                </div>
                {!showGroupPersonnels ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showGroupPersonnels ? (
                    <>
                        {treeData && treeData?.children?.length > 0 ? (
                            <Tree
                                treeData={treeData}
                                onToggle={handleToggle}
                                toggleState={toggleState}
                            />
                        ) : (
                            <div className="card card-shadow p-3">
                                <div className="alert alert-info d-flex mb-0">
                                    <div className="mr-2">
                                        <FontAwesomeIcon icon={FontAwesomeIcons.Solid.INFO_SQUARE} fixedWidth />
                                    </div>
                                    <span>There are currently no group personnel.</span>
                                </div>
                            </div>
                        )}
                    </>
                ) : null}
            </div>
        );
    }, [
        group,
        permissions,
        showGroupPersonnels,
        showCeased,
        treeData,
        handleToggle,
        toggleState,
    ]);

    return (
        <RestrictedLayout
            loading={loadingGroup || loadingTrialPersonnel}
            permission={Dtos.Permission.GroupPersonnelRoleView}
        >
            {renderContent}
        </RestrictedLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<GroupPersonnelsPageProps> => {
    let group: Dtos.Group | undefined = !(
        state.groups.formData instanceof Array
    )
        ? state.groups.formData
        : undefined;
    let trialPersonnel: Dtos.TrialPersonnel[] | undefined = (
        state.trialPersonnel.formData instanceof Array
    )
        ? state.trialPersonnel.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        group: group!,
        loadingGroup:
            state.groups.loadState &&
            state.groups.loadState.status == RequestState.Pending,
        loadGroupSuccess:
            state.groups.loadState &&
            state.groups.loadState.status == RequestState.Success,
        loadGroupFailure:
            state.groups.loadState &&
            state.groups.loadState.status == RequestState.Failure,
        loadGroupErrorCode: state.groups.loadState.errorCode,
        trialPersonnel: trialPersonnel!,
        loadingTrialPersonnel:
            state.trialPersonnel.loadState &&
            state.trialPersonnel.loadState.status == RequestState.Pending,
        loadTrialPersonnelSuccess:
            state.trialPersonnel.loadState &&
            state.trialPersonnel.loadState.status == RequestState.Success,
        loadTrialPersonnelFailure:
            state.trialPersonnel.loadState &&
            state.trialPersonnel.loadState.status == RequestState.Failure,
        loadTrialPersonnelErrorCode: state.trialPersonnel.loadState.errorCode,
        permissions: permissions,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<GroupPersonnelsPageProps> => {
    return {
        loadGroup: bindActionCreators(GroupActions.LoadGroupById, dispatch),
        clearGroup: bindActionCreators(GroupActions.Clear, dispatch),
        loadTrialPersonnels: bindActionCreators(TrialPersonnelActions.LoadTrialPersonnelsByGroupId, dispatch),
        clearTrialPersonnels: bindActionCreators(TrialPersonnelActions.Clear, dispatch),
        setBreadcrumbs: bindActionCreators(NavigationActions.SetBreadcrumbs, dispatch),
        setDrawerItems: bindActionCreators(NavigationActions.SetDrawerItems, dispatch)
    };
};

export default connect(mapStateToProps,  mapDispatchToProps)(GroupPersonnelsPage);
